import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { authRequest, changeAuthSession, clearStoredSession, onSessionExpired } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true), [restoreError, setRestoreError] = useState("");
  const [authMode, setAuthMode] = useState(null);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState(false);
  const [authenticatorEnrollment, setAuthenticatorEnrollment] = useState(null);
  const expiry = useRef(null), generation = useRef(0), deadline = useRef(0);
  const lock = useCallback(() => {
    generation.current += 1; changeAuthSession(); clearTimeout(expiry.current); deadline.current = 0;
    setUnlocked(false); setChecking(false); setRestoreError(""); setPasswordSetupRequired(false);
    setAuthenticatorEnrollment(null); setAuthMode(null);
  }, []);
  const applySession = useCallback((session) => {
    changeAuthSession(); clearTimeout(expiry.current); setAuthenticatorEnrollment(null);
    setAuthMode(session.auth_mode); setPasswordSetupRequired(session.password_setup_required);
    setUnlocked(!session.password_setup_required); setRestoreError(""); setChecking(false);
    deadline.current = Date.now() + session.expires_in * 1000;
    expiry.current = setTimeout(lock, session.expires_in * 1000);
  }, [lock]);
  const restore = useCallback(async () => {
    const id = ++generation.current;
    setChecking(true); setRestoreError("");
    try {
      const session = await authRequest("/auth/session");
      if (generation.current === id) applySession(session);
    } catch (error) {
      if (generation.current !== id) return;
      if (error.status === 401) lock();
      else { setRestoreError(error.message); setChecking(false); }
    }
  }, [applySession, lock]);
  const unlock = useCallback(async () => {
    const id = ++generation.current;
    // Confirm the browser accepted the HttpOnly cookie before displaying private data.
    try {
      const session = await authRequest("/auth/session");
      if (generation.current === id) applySession(session);
    } catch (error) {
      if (error.status === 401) throw new Error("Sign-in did not complete. Enter a fresh authenticator code and try again.");
      throw error;
    }
  }, [applySession]);
  const logout = useCallback(async () => {
    await authRequest("/auth/logout", {}).catch(() => {});
    clearStoredSession(); lock();
  }, [lock]);
  const beginAuthenticatorEnrollment = useCallback((enrollment) => {
    lock(); setAuthenticatorEnrollment(enrollment);
    expiry.current = setTimeout(lock, enrollment.expires_in * 1000);
  }, [lock]);
  useEffect(() => onSessionExpired(lock), [lock]);
  useEffect(() => { restore(); return () => { generation.current += 1; clearTimeout(expiry.current); }; }, [restore]);
  useEffect(() => {
    const resume = () => { if (deadline.current && Date.now() >= deadline.current) lock(); };
    window.addEventListener("focus", resume); document.addEventListener("visibilitychange", resume);
    return () => { window.removeEventListener("focus", resume); document.removeEventListener("visibilitychange", resume); };
  }, [lock]);

  return <AuthContext.Provider value={{ unlocked, checking, restoreError, restore, authMode, passwordSetupRequired, authenticatorEnrollment, beginAuthenticatorEnrollment, unlock, lock, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
